import type { PolicyModeType } from '@kirin/api-client/contracts/policies';
import {
  PolicyKey,
  PolicyMode,
  getPolicyCategoryLabel,
} from '@kirin/api-client/contracts/policies';
import { AutonomousExecutionSetting } from '@kirin/api-client/contracts/autonomous-execution';
import { AI_INSTRUCTION_POLICY_GROUP } from '@kirin/api-client/contracts/ai-instruction-catalog';

/**
 * Constants for policy settings
 * Centralized location for magic strings and special case identifiers
 *
 * Note: These are used as fallbacks when schema-based derivation fails.
 * Most field rendering is now derived from validation.pattern and validation.properties.
 */

/** Policies tagged "New" on the Policies page for their first release. */
export const NEW_POLICY_KEYS: ReadonlySet<string> = new Set([
  PolicyKey.AutonomousExecution,
]);

/**
 * KIR-3612: settings that only act while their policy is in Enforce (the
 * Autonomous Execution Mode enforce actions). They stay editable in every
 * mode, like other enforce-only settings, so an admin can set up the response
 * before switching to Enforce; a note says when they take effect.
 */
const ENFORCE_ONLY_SETTINGS: Readonly<Record<string, ReadonlySet<string>>> = {
  [PolicyKey.AutonomousExecution]: new Set([
    AutonomousExecutionSetting.RaiseSeverityWhileAutonomous,
    AutonomousExecutionSetting.ForceEnforcePolicies,
    AutonomousExecutionSetting.ForceEnforcePolicyKeys,
    AutonomousExecutionSetting.BlockYoloMode,
    AutonomousExecutionSetting.BlockClaudeAutoMode,
  ]),
};

export function isEnforceOnlySetting(
  policyKey: string,
  settingKey: string
): boolean {
  return ENFORCE_ONLY_SETTINGS[policyKey]?.has(settingKey) ?? false;
}

export const ENFORCE_ONLY_SETTING_NOTE =
  'Takes effect only while the policy is on Enforce.';

/** Display labels for Off / Monitor / Enforce policy modes */
export const MODE_LABELS: Record<PolicyModeType, string> = {
  [PolicyMode.Off]: 'Off',
  [PolicyMode.Monitor]: 'Monitor',
  [PolicyMode.Enforce]: 'Enforce',
};

/**
 * Narrows a stored mode-valued sub-policy setting (KIR-4426) onto a
 * `PolicyModeType`. Anything unrecognized — absent, `null`, or a legacy boolean
 * from an earlier toggle shape of the setting — reads as Off, matching how the
 * backend (`parseToolCallScanMode` in `v2/tool-call-scan/scan-tool-call.ts`)
 * and the Go agent (`hooks.ModeSettingOf`) narrow the same value, so the three
 * never disagree about what a malformed value means.
 *
 * Shared by `ModeSettingField` (Policies list) and `EditPolicyDialog` (Alerts),
 * which render the same sub-policy from two different data shapes.
 */
export function narrowPolicyMode(value: unknown): PolicyModeType {
  return value === PolicyMode.Monitor || value === PolicyMode.Enforce
    ? value
    : PolicyMode.Off;
}

/**
 * Why a nested mode sub-policy gets its own Off/Monitor/Enforce control.
 * Generic to any `type: 'mode'` setting — the whole point of the type is that
 * the sub-policy does not inherit its parent's mode.
 *
 * Lives here, not in `ModeSettingCard`, because the same sub-policy is also
 * rendered by `EditPolicyDialog` from the Alerts screen: two surfaces
 * explaining one control must not drift.
 */
export const MODE_SUB_POLICY_NOTE =
  'This sub-policy keeps its own Off / Monitor / Enforce mode so you can, for ' +
  'example, keep rules on Monitor while real-time analysis blocks the riskiest calls.';

/** Enforce-tab tooltip for a nested mode sub-policy. Shared for the same reason. */
export const MODE_SUB_POLICY_ENFORCE_TOOLTIP =
  'Enforce blocks flagged calls before they run, even when the policy above is ' +
  'only Monitor. Setting the policy above to Off disables this entirely. An ' +
  'analysis-service outage always fails open (the call is allowed).';

/**
 * KIR-4858: shown in place of {@link MODE_SUB_POLICY_NOTE} — and as the
 * disabled control's tooltip — while the parent policy is Off.
 *
 * The parent is a master switch, so the stored sub-mode is not in effect. The
 * control is displayed as Off but the value is deliberately NOT overwritten,
 * which would otherwise leave a mode the admin can no longer see waiting to
 * take effect the moment the parent is switched back on. Naming `retained`
 * here is what keeps that value visible instead of hidden.
 *
 * @param parentName Parent policy's display name; omitted falls back to a
 *   positional reference, since the selector always sits below it.
 * @param retained The mode that is being kept — the PENDING form value, not
 *   necessarily the persisted one, because that is what a save would write.
 */
export function modeSubPolicyDisabledNote(
  parentName: string | undefined,
  retained: PolicyModeType
): string {
  const subject = parentName ?? 'the policy above';
  // Second mention shortens to "it" only when the first one was a real name;
  // "the policy above … the policy above" would read worse than the repeat.
  const back = parentName ? 'it' : 'the policy above';
  if (retained === PolicyMode.Off) {
    return `Disabled while ${subject} is Off. Set ${back} to Monitor or Enforce to use real-time analysis.`;
  }
  return `Disabled while ${subject} is Off. Your ${MODE_LABELS[retained]} selection is kept and resumes when ${back} is set to Monitor or Enforce.`;
}

/**
 * Special setting keys that require custom rendering or validation
 * Used as fallback when schema derivation doesn't work
 */
export const SPECIAL_SETTINGS = {
  AI_CODE_SENSITIVITY: 'aiCodeSensitivity',
} as const;

/**
 * Policy keys that require special handling
 * Used as fallback when schema derivation doesn't work
 */
export const POLICY_KEYS = {
  EXTENSION_SECURITY: 'extension-security',
} as const;

/**
 * Allow/Block policies (KIR-1038 MCPs, KIR-1647 Extensions): no policy-level
 * Off/Monitor/Enforce on the collapsed row — tri-state belongs on the **Blocked** list inside settings (TODO there).
 */
export const POLICY_KEYS_HIDE_OUTER_MODE_CONTROLS: readonly string[] = [
  'extensions-management',
  'mcp-server-sources',
] as const;

export function policyKeyHidesOuterModeControls(policyKey: string): boolean {
  return (POLICY_KEYS_HIDE_OUTER_MODE_CONTROLS as readonly string[]).includes(
    policyKey
  );
}

/**
 * Backend `PolicyCategory.SECURE_AI_CODING` value (KIR-3816). The Secure AI
 * Coding Enforcement policy has its own top-level page, so `PoliciesList`
 * excludes this category from the Policies list. Named here to avoid a bare
 * string literal in the filter; drift from the backend value is guarded by the
 * exclusion test in `PoliciesList.test.tsx`.
 */
export const SECURE_AI_CODING_POLICY_CATEGORY = 'secure-ai-coding-policies';

/**
 * Backend `PolicyCategory.THREAT_MODELING` value (KIR-3925). Same deal as
 * Secure AI Coding: the Threat Modeling policy has its own AI AppSec page, so
 * `PoliciesList` excludes this category from the Policies list.
 */
export const THREAT_MODELING_POLICY_CATEGORY = 'threat-modeling-policies';

// KIR-3982: each category below owns a Policies sub-page; other categories fall
// to the default Detection page. `PoliciesList` filters by PolicySection.
export const ALLOW_BLOCK_POLICY_CATEGORY = 'configuration-policies';
export const AI_INSTRUCTION_POLICY_CATEGORY = 'ai-instruction-policies';

/** Categories that own a dedicated sub-page; everything else lands on Detection. */
export const NON_DETECTION_POLICY_CATEGORIES: readonly string[] = [
  AI_INSTRUCTION_POLICY_CATEGORY,
  ALLOW_BLOCK_POLICY_CATEGORY,
];

/** Which Policies sub-page `PoliciesList` renders. */
export type PolicySection = 'detection' | 'ai-instructions' | 'allow-block';

export type PolicyCategoryMetadata = Record<
  string,
  { name: string; description: string; order: number }
>;
// KIR-4432: the group *names* now come from `POLICY_CATEGORY_LABELS` in the
// api-client contract (via `getPolicyCategoryLabel`), which the backend registry and the public `/api/v1`
// `categoryName` field also read. They were previously written out here, in
// `buildPolicyGroups()`, and nowhere at all in the public contract — three
// copies of one label, and the API published the bare key. Descriptions stay
// local: this page's copy is longer than the group heading the API needs.
export const POLICY_CATEGORIES_META: PolicyCategoryMetadata = {
  'attack-detection-policies': {
    name: getPolicyCategoryLabel('attack-detection-policies'),
    description:
      'Detects active attacks, malicious content, and security threats. These policies are enabled by default to provide immediate protection.',
    order: 1,
  },
  // KIR-3515: shown below Detection Policies, above Allow/Block Lists.
  // Name + description are single-sourced from the api-client contract so they
  // can't drift from the backend policy group.
  'ai-instruction-policies': {
    name: AI_INSTRUCTION_POLICY_GROUP.name,
    description: AI_INSTRUCTION_POLICY_GROUP.description,
    order: 2,
  },
  'configuration-policies': {
    name: getPolicyCategoryLabel('configuration-policies'),
    description: 'Manage Allow and Block lists.',
    order: 3,
  },
};
